import {env} from '$env/dynamic/public';
import {toASCII, toUnicode} from 'punycode-esm';
import {tError, tSuccess} from './toast-utils';

let monthNameList = ['янв', 'фев', 'март', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

export function monthName(month: number): string {
    return monthNameList[month] ? monthNameList[month] : '' + month;
}

export function convertDateToUTC(date: Date) {
    return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds()
    );
}

export function leadZero(num: number): string {
    if (num < 10) {
        return '0' + num;
    }

    return '' + num;
}

/**
 * Удобный вывод даты
 */
export function date_pretty(date: Date | number | string, tzOffset: number | null = null): string {
    date = new Date(date);

    if (!(date instanceof Date) || (date.getTime && isNaN(date.getTime()))) {
        return '-';
    }

    if (typeof tzOffset !== 'number') {
        tzOffset = new Date().getTimezoneOffset();
    }

    date = convertDateToUTC(date);
    date.setTime(date.getTime() - tzOffset * 60 * 1000);

    const now = convertDateToUTC(new Date());
    now.setTime(now.getTime() - tzOffset * 60 * 1000);

    const time = leadZero(date.getHours()) + ':' + leadZero(date.getMinutes());

    // if (isToday(date)) {
    //     return time;
    // }

    const day = leadZero(date.getDate()) + ' ' + monthName(date.getMonth());
    const year = date.getFullYear();

    if (year === now.getFullYear()) {
        return day + ' ' + time;
    }

    return day + ' ' + year + ' ' + time;
}

export function days_remaing(date: Date | number | string, tzOffset: number | null = null): number {
    date = new Date(date);

    if (!(date instanceof Date) || (date.getTime && isNaN(date.getTime()))) {
        return 0;
    }

    if (typeof tzOffset !== 'number') {
        tzOffset = new Date().getTimezoneOffset();
    }

    date = convertDateToUTC(date);
    date.setTime(date.getTime() - tzOffset * 60 * 1000);

    const now = convertDateToUTC(new Date());
    now.setTime(now.getTime() - tzOffset * 60 * 1000);

    return Math.ceil((date.getTime() - now.getTime()) / 1000 / 60 / 60 / 24);
}

/**
 * Async timeout
 * @param delay
 */
export async function timeout(delay: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(() => resolve(), delay);
    });
}

/**
 * Нормализация домена
 */
export function normalizeDomain(domain: string): string {
    if (!domain) {
        return '';
    }

    domain = strTrim(domain, ':/ ');
    domain = domain.replace(/^((https?|ftps?):\/\/)?(www\.)?/g, '');
    domain = domain.split('/')[0];
    domain = strTrim(domain, '.:/ ');

    if (domain.indexOf('.') === -1) {
        return '';
    }

    return domain;
}

/**
 * trim символов по краям строки
 */
export function strTrim(str: string, chars: string = ' \t\n\r') {
    const charsList = chars.split('');

    let start = 0,
        end = str.length;

    while (start < end && charsList.includes(str[start])) ++start;

    while (end > start && charsList.includes(str[end - 1])) --end;

    return start > 0 || end < str.length ? str.substring(start, end) : str;
}

/**
 * Асинхронный интервал. Следующий запуск только после выполнения функции
 * Ошибки ловятся и не прерывают интервал
 * maxErrors указывает максимальное число ошибок, после которого все же выбросится исключение
 */
export function setAsyncInterval(fn: () => void, timeoutMs: number, maxErrors = 0): () => void {
    let errCount = 0;
    let stopped = false;
    setTimeout(async () => {
        do {
            if (stopped) {
                break;
            }

            try {
                await fn();
            } catch (e) {
                console.error(`Error in async interval ${errCount}/${maxErrors}`, e);

                errCount++;
                if (maxErrors > 0 && errCount >= maxErrors) {
                    throw e;
                }
            }

            await timeout(timeoutMs);
        } while (true);
    }, 0);

    return () => {
        stopped = true;
    };
}

/**
 * Парсинг JWT без верификации
 */
export function parseJwt(token: string) {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join('')
    );

    return JSON.parse(jsonPayload);
}

export function id_str_num(id: string): number {
    return parseInt(('' + id).substr(2), 10);
}

export function id_num_str(id: number | string): string {
    return 'id' + id;
}

/**
 * Копирование текста в буфер обмена
 */
export default function copyToClipboard(text: string, message = 'Скопировано в буфер обмена') {
    const el: any = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();

    if (document.execCommand) {
        document.execCommand('copy');
        document.body.removeChild(el);

        if (message) {
            tSuccess(message);
        }

        return true;
    } else if (el.copyText) {
        el.copyText();
        document.body.removeChild(el);

        if (message) {
            tSuccess(message);
        }

        return true;
    } else {
        document.body.removeChild(el);
        tError('Ваш браузер не поддерживает данную функцию. Произведите копирование вручную');
        return false;
    }
}

/**
 * Получение ссылки на изображение заданного размера
 */
export function getImageLink(image: GalaxyImagePG, method?: string): string {
    if (!image) {
        return '';
    }

    let [id, extension] = image.split(':', 2);

    if (!id || !extension) {
        console.warn('Wrong image', image);
        return '';
    }

    let url = env.PUBLIC_GALAXY_URL;

    if (method) {
        url += '/' + method;
    } else {
        url += '/original';
    }

    id = str_pad(id, 15, '0', 'STR_PAD_RIGHT');

    url += '/' + id.substr(0, 4);
    url += '/' + id.substr(4, 4);
    url += '/' + id.substr(8, 4);
    url += '/' + id.substr(12, 3);
    url += '.' + extension;

    return url;
}

export function str_pad(
    input: string,
    pad_length: number,
    pad_string: string,
    pad_type: 'STR_PAD_LEFT' | 'STR_PAD_RIGHT' | 'STR_PAD_BOTH'
) {
    // Pad a string to a certain length with another string
    //
    // +   original by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
    // + namespaced by: Michael White (http://crestidg.com)

    let half = '',
        pad_to_go;

    let str_pad_repeater = function (s: string, len: number) {
        let collect = '';

        while (collect.length < len) {
            collect += s;
        }
        collect = collect.substr(0, len);

        return collect;
    };

    if (pad_type !== 'STR_PAD_LEFT' && pad_type !== 'STR_PAD_RIGHT' && pad_type !== 'STR_PAD_BOTH') {
        pad_type = 'STR_PAD_RIGHT';
    }

    pad_to_go = pad_length - input.length;

    if (pad_to_go > 0) {
        if (pad_type === 'STR_PAD_LEFT') {
            input = str_pad_repeater(pad_string, pad_to_go) + input;
        } else if (pad_type === 'STR_PAD_RIGHT') {
            input = input + str_pad_repeater(pad_string, pad_to_go);
        } else if (pad_type === 'STR_PAD_BOTH') {
            half = str_pad_repeater(pad_string, Math.ceil(pad_to_go / 2));
            input = half + input + half;
            input = input.substr(0, pad_length);
        }
    }

    return input;
}

/**
 * Склонение слов в зависимости от количества
 * @param cnt
 * @param words
 * @param implode
 * @returns {string}
 */
export function decliner(cnt: number, words: [string, string, string], implode = false) {
    let cnt100 = Math.abs(cnt) % 100;
    let $n1 = cnt % 10;

    if (cnt100 > 10 && cnt100 < 20) {
        return implode === true ? cnt + ' ' + words[2] : words[2];
    } else if ($n1 > 1 && $n1 < 5) {
        return implode === true ? cnt + ' ' + words[1] : words[1];
    } else if ($n1 === 1) {
        return implode === true ? cnt + ' ' + words[0] : words[0];
    }

    return implode === true ? cnt + ' ' + words[2] : words[2];
}

export type PaginationStruct = {
    list: (PaginationPage | {type: 'ellipsis'})[];
    prev: PaginationPage | undefined;
    next: PaginationPage | undefined;
    current: PaginationPage;
    last: PaginationPage;
};

type PaginationPage = {
    type: 'number';
    page: number;
    first: boolean;
    last: boolean;
    active: boolean;
    url: string;
};

export function getPagination(
    currPage: number,
    onPage: number,
    total: number,
    urlBase: string,
    urlParam = 'page',
    maxShown = 6
): PaginationStruct {
    const pagesList: (PaginationPage | {type: 'ellipsis'})[] = [];
    const activePages = [];
    const countPages = Math.ceil(total / onPage);
    const firstPage = 1;
    const lastPage = countPages;

    const baseUrlParsed = new URL(urlBase, env.PUBLIC_Q_URL);

    const getUrl = function (pageIndex: number) {
        if (pageIndex > 1) {
            baseUrlParsed.searchParams.set(urlParam, '' + pageIndex);
        } else {
            baseUrlParsed.searchParams.delete(urlParam);
        }

        return baseUrlParsed.toString().substring(baseUrlParsed.origin.length);
    };

    if (countPages > maxShown) {
        let maxPart = Math.floor(maxShown / 3) - 1;

        if (maxPart < 1) {
            maxPart = 1;
        }

        for (let $i = 1; $i <= maxPart; $i++) {
            activePages.push($i);
        }

        for (let i = lastPage; i > lastPage - maxPart; i--) {
            activePages.push(i);
        }

        if (currPage <= maxPart || currPage >= lastPage - maxPart) {
            maxPart++;
        }

        for (let i = currPage - maxPart; i <= currPage + maxPart; i++) {
            activePages.push(i);
        }
    } else {
        for (let i = firstPage; i <= lastPage; i++) {
            activePages.push(i);
        }
    }

    for (let i = firstPage; i <= lastPage; i++) {
        if (!activePages.includes(i)) {
            if (pagesList[pagesList.length - 1].type !== 'ellipsis') {
                pagesList.push({type: 'ellipsis'});
            }

            continue;
        }

        pagesList.push({
            type: 'number',
            page: i,
            first: i === firstPage,
            last: i === lastPage,
            active: i === currPage,
            url: getUrl(i),
        });
    }

    let prevPage: PaginationPage | undefined = undefined;
    let nextPage: PaginationPage | undefined = undefined;

    if (currPage > 1) {
        prevPage = {
            type: 'number',
            page: currPage - 1,
            first: currPage - 1 === firstPage,
            last: currPage - 1 === lastPage,
            active: false,
            url: getUrl(currPage - 1),
        };
    }

    if (currPage < lastPage) {
        nextPage = {
            type: 'number',
            page: currPage + 1,
            first: currPage + 1 === firstPage,
            last: currPage + 1 === lastPage,
            active: false,
            url: getUrl(currPage + 1),
        };
    }

    return {
        list: pagesList,
        prev: prevPage,
        next: nextPage,
        current: {
            type: 'number',
            page: currPage,
            first: currPage === firstPage,
            last: currPage === lastPage,
            active: true,
            url: getUrl(currPage),
        },
        last: {
            type: 'number',
            page: lastPage,
            first: lastPage === firstPage,
            last: true,
            active: false,
            url: getUrl(lastPage),
        },
    };
}

export function logsLink(job: string) {
    return `${import.meta.env.VITE_K8S_LOGS_URL}/logs/${env.PUBLIC_LOGS_NAMESPACE}/job/${job}`;
}

/**
 * Парсинг дробных чисел
 * @param value
 */
export function parseFloatEx(value: string): number {
    return parseFloat(('' + value).replace(',', '.'));
}

/**
 * Перемещение элемента в массиве
 */
export function moveArrayElement<T>(input: T[], from: number, to: number): T[] {
    input = [...input];

    if (from < 0 || from > input.length - 1) {
        throw new Error(`{from} is out of bounds`);
    }

    if (to < 0) {
        to = input.length - 1;
    } else if (to > input.length - 1) {
        to = 0;
    }

    if (from === to) {
        return input;
    }

    const elm = input.splice(from, 1)[0];
    input.splice(to, 0, elm);
    return input;
}

/**
 * Обычный debounce
 */
export const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
    let timeout: any;

    return (...args: Parameters<F>): Promise<ReturnType<F>> =>
        new Promise(resolve => {
            if (timeout) {
                clearTimeout(timeout);
            }

            timeout = setTimeout(() => resolve(func(...args)), waitFor);
        });
};

/**
 * Выполнение асинхронной функции наплевав на ошибки и с таймаутом
 * @param fn
 * @param timeout
 */
export async function promiseTimeout(fn: () => Promise<void>, timeout = 500): Promise<void> {
    return new Promise(resolve => {
        let done = false;
        const myResolve = () => {
            if (!done) {
                done = true;
                resolve();
            }
        };

        fn().then(
            () => {
                myResolve();
            },
            e => {
                console.error('Ошибка в promiseTimeout', e);
                myResolve();
            }
        );

        setTimeout(() => {
            myResolve();
        }, timeout);
    });
}

/**
 * Генерация id для задач сбора списка категорий
 * @param domain
 * @param cred_id
 * @param options
 */
export function gen_website_category_queue_id(domain: string, cred_id?: string, options?: {[key: string]: string | null}) {
    let id = domain_to_id(domain);

    if (cred_id) {
        id += '-' + simple_str_hash(cred_id);
    }

    if (options) {
        id += '-' + simple_str_hash(JSON.stringify(options));
    }

    return id.toLowerCase().replace(/[^a-z0-9]/g, '-');
}

function simple_str_hash(str: string): number {
    if (str.length == 0) {
        return 0;
    }

    let hash = 0;

    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash = hash & hash;
    }

    return hash;
}

/**
 * Перевод домена в id mongo
 * @param domain
 */
export function domain_to_id(domain: string): string {
    domain = domain.toLowerCase();
    domain = toASCII(domain);
    return domain.replace(/[^a-z0-9]/g, '-');
}

/**
 * Нормализация поискового запроса
 */
export function normalizeQuery(query: string): string {
    const urlParse = new URL(query, env.PUBLIC_Q_URL);

    if (urlParse.host && query.includes(urlParse.host)) {
        query = urlParse.host;
    }

    if (query.indexOf('://') !== -1) {
        query = query.replace(/(https?|ftp)?:\/\//giu, '');
    }

    query = query.replace(/(\.\w{2,5})\/.+/giu, '$1');

    if (query.indexOf('xn--') !== -1) {
        query = toUnicode(query);
    }

    query = query.replace('www.', '');
    query = strTrim(query, ':/\\ ');

    return query;
}

export function preg_quote(str: string) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface InnValidationError {
    code: number;
    message: string;
}

/**
 * Валидация ИНН (Идентификационный номер налогоплательщика)
 * Проверяет корректность 10-значного (для юр. лиц) или 12-значного (для физ. лиц) ИНН
 * путем расчета контрольных цифр по алгоритму ФНС России
 *
 * @param inn - ИНН для проверки (строка или число)
 * @returns Объект с результатом валидации и описанием ошибки при наличии
 *
 * Коды ошибок:
 * 1 - ИНН пуст
 * 2 - ИНН содержит недопустимые символы
 * 3 - Неверная длина ИНН
 * 4 - Неверное контрольное число
 *
 * Основано на: http://www.kholenkov.ru/data-validation/inn/
 */
export function validateInn(inn: string | number): {valid: boolean; error?: InnValidationError} {
    const innString = typeof inn === 'number' ? inn.toString() : typeof inn === 'string' ? inn : '';

    if (!innString.length) {
        return {valid: false, error: {code: 1, message: 'ИНН пуст'}};
    }

    if (/[^0-9]/.test(innString)) {
        return {valid: false, error: {code: 2, message: 'ИНН может состоять только из цифр'}};
    }

    if (innString.length !== 10 && innString.length !== 12) {
        return {valid: false, error: {code: 3, message: 'ИНН может состоять только из 10 или 12 цифр'}};
    }

    // Проверка на ИНН из всех нулей
    if (/^0+$/.test(innString)) {
        return {valid: false, error: {code: 4, message: 'Неправильное контрольное число'}};
    }

    const checkDigit = (inn: string, coefficients: number[]): number => {
        const sum = coefficients.reduce((acc, coef, index) => {
            const digit = inn.charAt(index);
            return acc + coef * parseInt(digit, 10);
        }, 0);
        return (sum % 11) % 10;
    };

    let isValid = false;

    if (innString.length === 10) {
        const n10 = checkDigit(innString, [2, 4, 10, 3, 5, 9, 4, 6, 8]);
        isValid = n10 === parseInt(innString.charAt(9), 10);
    } else if (innString.length === 12) {
        const n11 = checkDigit(innString, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        const n12 = checkDigit(innString, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        isValid = n11 === parseInt(innString.charAt(10), 10) && n12 === parseInt(innString.charAt(11), 10);
    }

    if (!isValid) {
        return {valid: false, error: {code: 4, message: 'Неправильное контрольное число'}};
    }

    return {valid: true};
}
